import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { InternalOverviewResponse, DateRangeKey } from '../types/internal';
import { getErrorMessage } from '@/utils/error';

export const useInternalDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<InternalOverviewResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRangeKey>('30d');

    const getDateFilter = useCallback(() => {
        const now = new Date();
        const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
        const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        return { from: from.toISOString(), to: now.toISOString() };
    }, [dateRange]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        const { from, to } = getDateFilter();
        try {
            const response = await api.get<InternalOverviewResponse>('/dashboard/internal', {
                params: { from, to }
            });
            setData(response.data);
        } catch (err: unknown) {
            console.error('Failed to fetch internal dashboard:', err);
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [getDateFilter]);

    useEffect(() => {
        fetchData();

        // Auto-refresh data every 60 seconds
        const interval = setInterval(fetchData, 60_000);
        return () => clearInterval(interval);
    }, [fetchData]);

    return {
        loading,
        data,
        error,
        dateRange,
        setDateRange,
        refresh: fetchData
    };
};
